'use client';

import { useTranslations } from 'next-intl';
import { Icon, IconType } from '@/components/Icon';

const STAR_FILLED = '#FDBB2E';
const STAR_EMPTY = '#D9D9D9';

const HalfStar = ({ size }: { size: number }) => (
  <span className="relative inline-block" style={{ width: size, height: size }}>
    <span
      className="absolute top-0 left-0"
      style={{ width: size, height: size, color: STAR_EMPTY }}
    >
      <Icon src={IconType.SvgStarHolo} size={size} />
    </span>
    <span
      className="absolute top-0 left-0 overflow-hidden"
      style={{ width: size / 2, height: size, color: STAR_FILLED }}
    >
      <Icon src={IconType.SvgStarFilled} size={size} />
    </span>
  </span>
);

export const StarRating = ({
  rating,
  size = 20,
}: {
  rating: number | null;
  size?: number;
}) => {
  const t = useTranslations('reviews');
  const clamped = Math.max(0, Math.min(5, rating ?? 0));
  const full = Math.floor(clamped);
  const hasHalf = clamped % 1 >= 0.3;
  const empty = 5 - full - (hasHalf ? 1 : 0);

  return (
    <div
      className="flex items-center gap-0.5"
      role="img"
      aria-label={t('starsAriaLabel', { rating: clamped.toFixed(1) })}
    >
      {Array.from({ length: full }, (_, i) => (
        <span
          key={`f-${i}`}
          className="inline-block"
          style={{ color: STAR_FILLED, lineHeight: 0 }}
        >
          <Icon src={IconType.SvgStarFilled} size={size} />
        </span>
      ))}
      {hasHalf && <HalfStar size={size} />}
      {Array.from({ length: empty }, (_, i) => (
        <span
          key={`e-${i}`}
          className="inline-block"
          style={{ color: STAR_EMPTY, lineHeight: 0 }}
        >
          <Icon src={IconType.SvgStarHolo} size={size} />
        </span>
      ))}
    </div>
  );
};
